import { mutationTypes } from './types'
import { DataEntry, Reports } from '@/api'
import * as extractor from '@/data-extraction/reports'
import { CONST } from '@/utils/constants'
import { EditCell } from '@/data-entry'
import toCss from 'to-css'
import { CssMapper } from '@/mapper'
import { replaceGridStylesCss } from '@/dom'
import { validateReportTotal } from '@/clipboard/limits'
import { readOnlyModeErrorMessage } from '@/utils'
import { Modes } from '@/utils/enums'

export default {
  resetState: ({ commit }) => {
    commit(mutationTypes.RESET_STATE)
  },
  setDirty: ({ commit }, dirty) => {
    commit(mutationTypes.SET_DIRTY, dirty)
  },
  loadingFinished: ({ commit }) => {
    commit(mutationTypes.LOADING_FINISHED)
  },
  setShowingUnsavedDialog: ({ commit }, showingUnsavedDialog) => {
    commit(mutationTypes.SET_SHOWING_UNSAVED_DIALOG, showingUnsavedDialog)
  },
  setSelectedRange: ({ commit }, selectedRange) => {
    commit(mutationTypes.SET_SELECTED_RANGE, selectedRange)
  },
  setTitle: ({ commit }, title) => {
    commit(mutationTypes.SET_TITLE, title)
  },
  setSubtitle: ({ commit }, subtitle) => {
    commit(mutationTypes.SET_SUBTITLE, subtitle)
  },
  addError: ({ commit }, err) => {
    commit(mutationTypes.ADD_ERROR, err)
  },
  removeError: ({ commit }, err) => {
    commit(mutationTypes.REMOVE_ERROR, err)
  },
  setReadOnlyMode: ({ commit }) => {
    commit(mutationTypes.SET_READ_ONLY_MODE)
  },
  switchToReadOnlyMode: async ({ commit, getters, rootGetters }) => {
    try {
      commit(mutationTypes.SWITCH_TO_READ_ONLY_MODE_PENDING)
      await Reports.switchToReadOnlyMode(rootGetters['user/userId'], getters.managerId)
      commit(mutationTypes.SWITCH_TO_READ_ONLY_MODE_SUCCESS)
    } catch (err) {
      commit(mutationTypes.SWITCH_TO_READ_ONLY_MODE_FAILURE, err)
    }
  },
  clearCells: ({ commit, getters, rootGetters }) => {
    commit(mutationTypes.CLEAR_CELLS_PENDING)
    return DataEntry.clearCells(rootGetters['user/userId'], getters.managerId, EditCell.getClearCells(getters.selectedRange))
      .then((response) => {
        commit(mutationTypes.CLEAR_CELLS_SUCCESS)
        return response
      })
      .catch((err) => {
        commit(mutationTypes.CLEAR_CELLS_FAILURE, err)
        throw err
      })
  },
  clearCell: ({ commit, getters, rootGetters }) => {
    commit(mutationTypes.CLEAR_CELL_PENDING)
    return DataEntry.clearCell(rootGetters['user/userId'], getters.managerId, EditCell.getClearCell(getters.selectedRange))
      .then((response) => {
        commit(mutationTypes.CLEAR_CELL_SUCCESS)
        return response
      })
      .catch((err) => {
        commit(mutationTypes.CLEAR_CELL_FAILURE, err)
        throw err
      })
  },
  save: ({ commit, getters, rootGetters }) => {
    commit(mutationTypes.SAVING_PENDING)
    return Reports.saveSession(rootGetters['user/userId'], getters.managerId)
      .then(() => commit(mutationTypes.SAVING_SUCCESS))
      .catch((err) => {
        commit(mutationTypes.SAVING_FAILURE, err)
        throw err
      })
  },
  getReport: ({ getters, rootGetters }, {start, count, refresh = false}) => {
    return Reports.getReport(rootGetters['user/userId'], getters.managerId, start, count, refresh)
      .then((report) => {
        return report
      })
  },
  loadReport: async ({ commit, getters, rootGetters }) => {
    try {
      commit(mutationTypes.LOAD_REPORT_PENDING)
      let report = await Reports.openReport({ userId: rootGetters['user/userId'], managerId: getters.managerId, start: 0, count: CONST.Grid.PageSize })
      const columnHeaderMetaData = extractor.extractColumnHeaderStyles(report)
      const cellStyles = toCss(CssMapper.mapStylesToCssObjects(extractor.extractStyles(report)))
      const columnHeaderStyles = toCss(CssMapper.mapStylesToCssObjects(columnHeaderMetaData))
      const allStyles = cellStyles.concat(columnHeaderStyles)
      const total = extractor.extractTotal(report)
      const title = extractor.extractTitle(report)
      const subTitle = extractor.extractSubtitle(report)
      const dirty = extractor.extractDirty(report)
      const canInsert = extractor.extractCanInsert(report)
      const columns = extractor.extractColumns(report)
      const mode = extractor.extractMode(report)
      validateReportTotal(total)
      replaceGridStylesCss(allStyles)
      commit(mutationTypes.LOAD_REPORT_SUCCESS, {
        total,
        title,
        subTitle,
        dirty,
        columnHeaderMetaData,
        canInsert,
        columns,
        mode
      })
      return report
    } catch (err) {
      commit(mutationTypes.LOAD_REPORT_FAILURE, err)
      throw err
    }
  },
  insert: ({ commit, getters, rootGetters }, row) => {
    commit(mutationTypes.INSERTING_PENDING)
    return DataEntry.insertRow(rootGetters['user/userId'], getters.managerId, row)
      .then(() => commit(mutationTypes.INSERTING_SUCCESS))
      .catch((err) => {
        commit(mutationTypes.INSERTING_FAILURE, err)
        throw err
      })
  },
  editCell: async ({commit, getters, rootGetters}, {row, col, data}) => {
    try {
      commit(mutationTypes.EDIT_CELL_PENDING, {row, col})
      let value = await DataEntry.editCell({userId: rootGetters['user/userId'], managerId: getters.managerId, rowIndex: row, columnIndex: col, data: data})
      commit(mutationTypes.EDIT_CELL_SUCCESS, {row, col})
      return value
    } catch (error) {
      commit(mutationTypes.EDIT_CELL_FAILURE, {row, col, error})
      if (error.response !== undefined) {
        return error.response.data.value
      }
    }
  },
  refreshMetadata: ({ commit, getters, rootGetters }) => {
    commit(mutationTypes.REFRESH_METADATA_PENDING)
    return Reports.getReport(rootGetters['user/userId'], getters.managerId, 0, 1, false)
      .then(report => {
        if (extractor.extractIsInReadOnlyMode(report)) {
          const text = readOnlyModeErrorMessage()
          commit(mutationTypes.REFRESH_METADATA_FAILURE, {error: new Error(text), mode: Modes.ReadOnly})
        } else {
          commit(mutationTypes.REFRESH_METADATA_SUCCESS, {
            total: extractor.extractTotal(report),
            // the api does not return a correct state for dirty if a clear cells has been performed on a comments columns. It returns false when it should be true, this check prevents us overwritting dirty when we know it is dirty
            dirty: getters.dirty || extractor.extractDirty(report)
          })
        }
      })
      .catch(error => {
        commit(mutationTypes.REFRESH_METADATA_FAILURE, {error, mode: Modes.Unknown})
      })
  },
  refresh: ({ commit, getters, rootGetters }) => {
    commit(mutationTypes.REFRESH_PENDING)
    return Reports.getReport(rootGetters['user/userId'], getters.managerId, 0, CONST.Grid.PageSize, false)
      .then(report => {
        if (extractor.extractIsInReadOnlyMode(report)) {
          const text = readOnlyModeErrorMessage()
          commit(mutationTypes.REFRESH_FAILURE, {error: new Error(text), mode: Modes.ReadOnly})
        } else {
          commit(mutationTypes.REFRESH_SUCCESS, {
            total: extractor.extractTotal(report),
            dirty: getters.dirty || extractor.extractDirty(report),
            columns: extractor.extractColumns(report)
          })
          return report
        }
      })
      .catch(error => {
        commit(mutationTypes.REFRESH_FAILURE, {error, mode: Modes.Unknown})
      })
  }
}
